import React, { Component } from "react";
import { connect } from "react-redux";
import Table from "../_common/Table";
import TitleBar from "../_common/TitleBar";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Nav, NavItem, NavLink, Collapse } from "reactstrap";
import DatePicker from "react-datepicker";
import CustomDatePickerInput from "../_common/CustomDatePickerInput";
import payEstimateListTitleBarMenu from "../_titleBarMenus/payEstimateListTitleBarMenu";
import { actionScripts } from "../../_helpers/actionScripts";
import payEstimateActions from "../../_actions/payEstimateActions";
import bidItemActions from "../../_actions/bidItemActions";
import { formatThousands } from "../../_helpers/numberFormatter";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "./payEstimate.css";

const CURRENT_PAY_PERIOD = (() => {
  const today = new Date();
  const day = today.getDate();
  const month = today.getMonth();
  const year = today.getFullYear();
  if (day >= 21) {
    return {
      start: new Date(year, month, 21),
      end: new Date(year, month + 1, 20),
    };
  } else {
    return {
      start: new Date(year, month - 1, 21),
      end: new Date(year, month, 20),
    };
  }
})();

const IS_PREFIX = "is_";

class PayEstimateSummary extends Component {
  state = {
    sortColumn: { path: "estimateNumber", order: "desc" },
    selectedEstimateId: null,
    showCreateModal: false,
    newPayPeriodStart: CURRENT_PAY_PERIOD.start,
    newPayPeriodEnd: CURRENT_PAY_PERIOD.end,
    // Item Summary tab
    activeTab: "estimates",
    itemSummaryLoaded: false,
    expandedItems: {},
    itemDescFilter: "",
  };

  componentDidMount() {
    const { projectId, dispatch } = this.props;
    if (projectId) {
      dispatch(payEstimateActions.fetchList(projectId));
    }
  }

  componentWillUnmount() {
    // Clean up item summary detail entries from the store
    const { dispatch, estimates } = this.props;
    (estimates || []).forEach((e) => {
      dispatch({ type: "PE.CLOSE_TAB", tabId: `${IS_PREFIX}${e.payEstimateId}` });
    });
  }

  columns = [
    {
      path: "estimateNumber",
      label: "Est #",
      content: (e) => <b>{e.estimateNumber}</b>,
      thClassName: "pe-col--no",
      tdClassName: "pe-col--no",
    },
    {
      path: "payPeriod",
      disableSort: true,
      label: "Pay Period",
      content: (e) => {
        const start = new Date(e.payPeriodStartDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
        const end = new Date(e.payPeriodEndDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
        return `${start} – ${end}`;
      },
      thClassName: "pe-col--period",
      tdClassName: "pe-col--period",
    },
    {
      path: "isClosed",
      label: "Status",
      content: (e) => (
        <span className={`span-bubble p-1 px-2 ${e.isClosed ? "bg-secondary" : "bg-primary"}`}>
          {e.isClosed ? "Closed" : "Open"}
        </span>
      ),
      thClassName: "pe-col--status",
      tdClassName: "pe-col--status",
    },
    {
      path: "estimateAmount",
      disableSort: true,
      label: "Amount",
      content: (e) =>
        e.estimateAmount != null
          ? e.estimateAmount.toLocaleString("en-US", { style: "currency", currency: "USD" })
          : "—",
      thClassName: "pe-col--est-amount",
      tdClassName: "pe-col--est-amount",
    },
    {
      path: "actions",
      disableSort: true,
      label: "",
      content: (e) =>
        !e.isClosed ? (
          <Button
            color="danger"
            size="sm"
            outline
            title="Delete this open estimate"
            onClick={(ev) => { ev.stopPropagation(); this.handleDeleteEstimate(e); }}
          >
            Delete
          </Button>
        ) : null,
      thClassName: "pe-col--actions",
      tdClassName: "pe-col--actions",
    },
  ];

  handleTitleBarAction = (action) => () => {
    switch (action) {
      case "newEstimate":
        this.setState({ showCreateModal: true });
        break;
      case "refresh":
        this.props.dispatch(payEstimateActions.fetchList(this.props.projectId));
        break;
      default:
        break;
    }
  };

  handleSort = (sortColumn) => this.setState({ sortColumn });

  handleRowClick = (estimate) => {
    this.setState({ selectedEstimateId: estimate.payEstimateId });
    actionScripts.OPEN_PAY_ESTIMATE_DETAIL.run(estimate, this.props.projectId);
  };

  handleDeleteEstimate = (estimate) => {
    if (!window.confirm(`Delete Estimate #${estimate.estimateNumber}? This cannot be undone.`)) return;
    const { projectId, dispatch } = this.props;
    dispatch(payEstimateActions.deleteEstimate(estimate.payEstimateId, projectId));
  };

  handleCreateSubmit = () => {
    const { newPayPeriodStart, newPayPeriodEnd } = this.state;
    const { projectId, dispatch, userId } = this.props;

    const dto = {
      projectId,
      payPeriodStartDate: newPayPeriodStart,
      payPeriodEndDate: newPayPeriodEnd,
      createdByUserId: userId,
    };

    dispatch(payEstimateActions.createEstimate(dto, projectId)).then(() => {
      dispatch(payEstimateActions.fetchList(projectId));
    });

    this.setState({ showCreateModal: false });
  };

  handleTabClick = (tab) => {
    this.setState({ activeTab: tab });
    if (tab === "item_summary" && !this.state.itemSummaryLoaded) {
      this.setState({ itemSummaryLoaded: true });
      const { projectId, dispatch, projItemList, estimates } = this.props;
      if (!projItemList) dispatch(bidItemActions.getAll(projectId));
      estimates.forEach((e) => {
        dispatch(payEstimateActions.fetchDetail(e.payEstimateId, `${IS_PREFIX}${e.payEstimateId}`));
      });
    }
  };

  handleExpandItem = (bidItemId) => {
    this.setState((prev) => ({
      expandedItems: { ...prev.expandedItems, [bidItemId]: !prev.expandedItems[bidItemId] },
    }));
  };

  render() {
    const {
      sortColumn,
      selectedEstimateId,
      showCreateModal,
      newPayPeriodStart,
      newPayPeriodEnd,
      activeTab,
      expandedItems,
      itemDescFilter,
    } = this.state;

    const { estimates = [], fetching, projItemList, allEstimateDetails } = this.props;

    const fmtDate = (d) =>
      new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

    const currentEstimate = estimates
      .filter((e) => !e.isClosed)
      .sort((a, b) => b.estimateNumber - a.estimateNumber)[0] || null;

    const { start: periodStart, end: periodEnd } = CURRENT_PAY_PERIOD;

    // ── Item Summary data ────────────────────────────────────────────────────
    const loadedCount = Object.keys(allEstimateDetails).length;
    const isItemSummaryLoading = !projItemList || (estimates.length > 0 && loadedCount < estimates.length);

    // Build payment map: bidItemId -> { paidToDate, estimateRows }
    // paidToDate = sum of totalEstimatedQuantity from CLOSED estimates only.
    // The open (current) estimate's quantity is not yet "paid to date".
    // estimateRows includes all non-draft QSheets (open + closed) for the expanded history.
    const itemPaymentMap = {};
    Object.values(allEstimateDetails).forEach((detail) => {
      if (!detail?.qSheets) return;
      detail.qSheets.forEach((qs) => {
        if (qs.isDraft) return;
        if (!itemPaymentMap[qs.bidItemId]) {
          itemPaymentMap[qs.bidItemId] = { paidToDate: 0, estimateRows: [] };
        }
        const entry = itemPaymentMap[qs.bidItemId];
        if (detail.isClosed) {
          entry.paidToDate += qs.totalEstimatedQuantity || 0;
        }
        entry.estimateRows.push({
          estimateNumber: detail.estimateNumber,
          payEstimateId: detail.payEstimateId,
          payPeriodStart: detail.payPeriodStartDate,
          payPeriodEnd: detail.payPeriodEndDate,
          isClosed: detail.isClosed,
          quantityPaid: qs.totalEstimatedQuantity || 0,
          unitOfMeasure: qs.unitOfMeasure,
        });
      });
    });
    // Sort per-estimate rows chronologically
    Object.values(itemPaymentMap).forEach((entry) => {
      entry.estimateRows.sort((a, b) => a.estimateNumber - b.estimateNumber);
    });

    const filteredItems = (projItemList || [])
      .filter((bi) => {
        if (!itemDescFilter) return true;
        if (itemDescFilter.match(/^\d{1,3}/)) return bi.itemNo == itemDescFilter;
        return bi.description.toLowerCase().includes(itemDescFilter.trim().toLowerCase());
      })
      .sort((a, b) => String(a.itemNo).localeCompare(String(b.itemNo), undefined, { numeric: true }));

    return (
      <React.Fragment>
        <TitleBar
          titleText="Pay Estimate Summary"
          menu={payEstimateListTitleBarMenu}
          handleTitleBarAction={this.handleTitleBarAction}
        />
        <div className="card m-2">
          <div className="card-body">

            {/* ── Tabs ─────────────────────────────────────────────────── */}
            <Nav tabs className="mb-3">
              <NavItem>
                <NavLink
                  className={activeTab === "estimates" ? "active" : ""}
                  style={{ cursor: "pointer" }}
                  onClick={() => this.handleTabClick("estimates")}
                >
                  Estimates
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={activeTab === "item_summary" ? "active" : ""}
                  style={{ cursor: "pointer" }}
                  onClick={() => this.handleTabClick("item_summary")}
                >
                  Item Summary
                </NavLink>
              </NavItem>
            </Nav>

            {/* ── Estimates Tab ─────────────────────────────────────────── */}
            {activeTab === "estimates" && (
              <React.Fragment>
                {/* Current Pay Period banner */}
                <div className="pe-current-period d-flex align-items-center justify-content-between mb-2 px-3 py-2 border rounded bg-light">
                  <div>
                    <span className="text-muted small text-uppercase fw-semibold me-2">Current Pay Period: </span>
                    <span className="fw-bold">
                      {fmtDate(periodStart)} – {fmtDate(periodEnd)}
                    </span>
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      color="success"
                      size="sm"
                      disabled={!!currentEstimate}
                      title={currentEstimate ? "Close the current open estimate before creating a new one." : undefined}
                      onClick={() => this.setState({ showCreateModal: true })}
                    >
                      + New Estimate
                    </Button>
                  </div>
                </div>

                <div className="adrlist-table-container1 border">
                  <Table
                    className="table table-hover pe-list-table w-100"
                    data={estimates}
                    idPropName="payEstimateId"
                    selectedItemId={selectedEstimateId}
                    columns={this.columns}
                    sortColumn={sortColumn}
                    onSort={this.handleSort}
                    handleClick_tr={this.handleRowClick}
                  />
                  {fetching && (
                    <div className="text-muted text-center p-2">Loading...</div>
                  )}
                </div>
              </React.Fragment>
            )}

            {/* ── Item Summary Tab ──────────────────────────────────────── */}
            {activeTab === "item_summary" && (
              <React.Fragment>
                {/* Search filter */}
                <div className="d-flex align-items-center mb-2">
                  <div className="input-group" style={{ maxWidth: 340 }}>
                    <div className="input-group-prepend">
                      <span className="input-group-text bg-lightgray">
                        <FontAwesomeIcon icon="search" className="fa-fw text-muted" />
                      </span>
                    </div>
                    <input
                      className="form-control"
                      value={itemDescFilter}
                      onChange={(e) => this.setState({ itemDescFilter: e.target.value })}
                      placeholder="Item # or Description"
                    />
                  </div>
                  {isItemSummaryLoading && (
                    <span className="ml-3 text-muted small">
                      <FontAwesomeIcon icon="spinner" spin className="mr-1" />
                      Loading estimate data… ({loadedCount}/{estimates.length})
                    </span>
                  )}
                </div>

                {/* Item table */}
                <div className="pe-item-summary-table border">
                  {/* Header */}
                  <div className="d-flex flex-row w-100 bg-lightgray pe-item-summary-header" style={{ zIndex: 9 }}>
                    <div className="p-1 pe-is-col--expand" />
                    <div className="p-1 pe-is-col--no"><b>No.</b></div>
                    <div className="p-1 pe-is-col--desc"><b>Description</b></div>
                    <div className="p-1 pe-is-col--uom"><b>Unit</b></div>
                    <div className="p-1 pe-is-col--qty text-right"><b>Bid Qty (EE)</b></div>
                    <div className="p-1 pe-is-col--qty text-right"><b>Auth. Qty</b></div>
                    <div className="p-1 pe-is-col--qty text-right"><b>(Qty) Paid to Date</b></div>
                    <div className="p-1 pe-is-col--qty text-right"><b>Qty Remaining</b></div>
                  </div>

                  {/* Body */}
                  <div>
                    {filteredItems.length === 0 && !isItemSummaryLoading && (
                      <div className="p-2 text-muted">{projItemList ? "(No items)" : "Loading…"}</div>
                    )}
                    {filteredItems.map((bi) => {
                      const payment = itemPaymentMap[bi.bidItemId];
                      const authQty = (bi.eeQuantity || 0) + (bi.ccoQuantity || 0);
                      const paidToDate = payment?.paidToDate || 0;
                      const qtyRemaining = authQty - paidToDate;
                      const isExpanded = expandedItems[bi.bidItemId] || false;

                      return (
                        <div key={`pe-is-row-${bi.bidItemId}`} className={`pe-is-item-row ${isExpanded ? "pe-is-item-row--expanded" : ""}`}>
                          {/* Main row */}
                          <div
                            className="d-flex flex-row pe-is-table-row"
                            onClick={() => this.handleExpandItem(bi.bidItemId)}
                            style={{ cursor: "pointer" }}
                          >
                            <div className="p-1 pe-is-col--expand">
                              <FontAwesomeIcon
                                icon={isExpanded ? "angle-down" : "angle-right"}
                                className="fa-fw mx-1 align-middle"
                                style={{ fontSize: "1.1rem" }}
                              />
                            </div>
                            <div className="p-1 pe-is-col--no">
                              <span className={`align-middle ${isExpanded ? "font-weight-bold" : ""}`}>
                                <small>#</small>{bi.itemNo}
                              </span>
                            </div>
                            <div className="p-1 pe-is-col--desc">
                              <span className={`align-middle ${isExpanded ? "font-weight-bold" : ""}`}>
                                {bi.description}
                              </span>
                            </div>
                            <div className="p-1 pe-is-col--uom align-middle">{bi.unitOfMeasure}</div>
                            <div className="p-1 pe-is-col--qty text-right align-middle">
                              {formatThousands(bi.eeQuantity, 3)}
                            </div>
                            <div className="p-1 pe-is-col--qty text-right align-middle">
                              {formatThousands(authQty, 3)}
                            </div>
                            <div className="p-1 pe-is-col--qty text-right align-middle">
                              {isItemSummaryLoading
                                ? <span className="text-muted">…</span>
                                : formatThousands(paidToDate, 3)
                              }
                            </div>
                            <div className={`p-1 pe-is-col--qty text-right align-middle ${qtyRemaining < 0 ? "text-danger font-weight-bold" : ""}`}>
                              {isItemSummaryLoading
                                ? <span className="text-muted">…</span>
                                : formatThousands(qtyRemaining, 3)
                              }
                            </div>
                          </div>

                          {/* Expanded sub-rows: per-estimate payment history */}
                          <Collapse isOpen={isExpanded}>
                            <div className="pe-is-sub-table border-top ml-3">
                              {!payment || payment.estimateRows.length === 0 ? (
                                <div className="p-2 text-muted small fst-italic">
                                  {isItemSummaryLoading ? "Loading..." : "No payments recorded for this item."}
                                </div>
                              ) : (
                                <React.Fragment>
                                  {/* Sub-header */}
                                  <div className="d-flex flex-row bg-light border-bottom pe-is-sub-header">
                                    <div className="px-2 py-1 pe-is-sub-col--est"><b>Est #</b></div>
                                    <div className="px-2 py-1 pe-is-sub-col--period"><b>Pay Period</b></div>
                                    <div className="px-2 py-1 pe-is-sub-col--qty text-right"><b>Qty Paid</b></div>
                                  </div>
                                  {payment.estimateRows.map((row) => (
                                    <div
                                      key={`pe-is-sub-${bi.bidItemId}-${row.payEstimateId}`}
                                      className={`d-flex flex-row border-bottom pe-is-sub-row ${!row.isClosed ? "text-muted fst-italic" : ""}`}
                                    >
                                      <div className="px-2 py-1 pe-is-sub-col--est">#{row.estimateNumber}</div>
                                      <div className="px-2 py-1 pe-is-sub-col--period">
                                        {fmtDate(row.payPeriodStart)} – {fmtDate(row.payPeriodEnd)}
                                        {!row.isClosed && <span className="ml-2 badge badge-warning">Open</span>}
                                      </div>
                                      <div className="px-2 py-1 pe-is-sub-col--qty text-right">
                                        {formatThousands(row.quantityPaid, 3)}
                                        {row.unitOfMeasure ? ` ${row.unitOfMeasure}` : ""}
                                      </div>
                                    </div>
                                  ))}
                                </React.Fragment>
                              )}
                            </div>
                          </Collapse>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </React.Fragment>
            )}
          </div>
        </div>

        {/* ── Create Pay Estimate Modal ────────────────────────────────── */}
        <Modal
          isOpen={showCreateModal}
          toggle={() => this.setState({ showCreateModal: false })}
          fade={false}
        >
          <ModalHeader toggle={() => this.setState({ showCreateModal: false })}>
            New Pay Estimate
          </ModalHeader>
          <ModalBody>
            <p className="text-muted small mb-3">
              Select the pay period for this estimate. Defaults to the current
              period (21st of previous month – 20th of current month).
            </p>
            <div className="form-group row align-items-center">
              <label className="col-4 col-form-label">
                <b>Period Start:</b>
              </label>
              <div className="col-7">
                <DatePicker
                  selected={newPayPeriodStart}
                  onChange={(date) => {
                    const end = new Date(date.getFullYear(), date.getMonth() + 1, date.getDate() - 1);
                    this.setState({ newPayPeriodStart: date, newPayPeriodEnd: end });
                  }}
                  dateFormat="MMM d, yyyy"
                  customInput={<CustomDatePickerInput />}
                />
              </div>
            </div>
            <div className="form-group row align-items-center">
              <label className="col-4 col-form-label">
                <b>Period End:</b>
              </label>
              <div className="col-7">
                <DatePicker
                  selected={newPayPeriodEnd}
                  onChange={(date) => {
                    const start = new Date(date.getFullYear(), date.getMonth() - 1, date.getDate() + 1);
                    this.setState({ newPayPeriodEnd: date, newPayPeriodStart: start });
                  }}
                  dateFormat="MMM d, yyyy"
                  customInput={<CustomDatePickerInput />}
                />
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              outline
              onClick={() => this.setState({ showCreateModal: false })}
            >
              Cancel
            </Button>
            <Button color="primary" onClick={this.handleCreateSubmit}>
              Create Estimate
            </Button>
          </ModalFooter>
        </Modal>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  // Collect item summary estimate details (keyed by "is_" + payEstimateId)
  const allEstimateDetails = {};
  Object.entries(state.payEstimate.openEstimateDetails)
    .filter(([k]) => k.startsWith(IS_PREFIX))
    .forEach(([k, v]) => {
      const payEstimateId = parseInt(k.slice(IS_PREFIX.length), 10);
      allEstimateDetails[payEstimateId] = v;
    });

  return {
    estimates: state.payEstimate.estimateLists[ownProps.projectId] || [],
    fetching: state.payEstimate.fetching,
    userId: state.authentication.user?.userId,
    projItemList: state.bidItems.projItemLists[ownProps.projectId] || null,
    allEstimateDetails,
  };
};

export default connect(mapStateToProps)(PayEstimateSummary);
